import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { ChatMessage, SuggestedAction, WorkflowStatus } from "../types/redux";
import {
  type AddGenerationProgressPayload,
  type ClearTurnTodoPlanPayload,
  type SetTurnTodoPlanPayload,
  type StageTurnResultsPayload,
  type UpdateGenerationStatusPayload,
  type UpdateMessagePayload,
  initialState,
  WELCOME_MESSAGE_TEXT,
} from "./chatSliceTypes";
import { applyUploadUpdate, type UploadUpdatePayload } from "./uploadTable";

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessageByClientId: (state, action: PayloadAction<UpdateMessagePayload>) => {
      const { clientMessageId, text, updates } = action.payload || {};
      if (!clientMessageId) return;
      const target = state.messages.find(
        (message) => message.clientMessageId === clientMessageId
      );
      if (!target) return;

      if (updates && typeof updates === "object") {
        Object.assign(target, updates);
      }

      if (text !== undefined) {
        target.text = text;
      }
    },
    removeMessageByClientId: (state, action: PayloadAction<string>) => {
      const clientMessageId = action.payload;
      if (!clientMessageId) return;
      state.messages = state.messages.filter(
        (message) => message.clientMessageId !== clientMessageId
      );
    },
    updateMessageText: (
      state,
      action: PayloadAction<{ index: number; text?: string; suggested_actions?: SuggestedAction[] }>
    ) => {
      const { index, text, suggested_actions } = action.payload;
      if (index >= 0 && index < state.messages.length) {
        if (text !== undefined) {
          state.messages[index].text = text;
        }
        if (suggested_actions && Array.isArray(suggested_actions)) {
          state.messages[index].suggested_actions = suggested_actions;
        }
      }
    },
    updateUploadInTable: (state, action: PayloadAction<UploadUpdatePayload>) => {
      applyUploadUpdate(state, action.payload);
    },
    clearUploadsTable: (state) => {
      state.messages = state.messages.filter((msg: ChatMessage) => msg.type !== "upload-table");
    },
    addGenerationProgress: (state, action: PayloadAction<AddGenerationProgressPayload>) => {
      const { projectId } = action.payload;
      // Check if a generation-progress message already exists for this project
      const existingProgressIndex = state.messages.findIndex(
        (msg: ChatMessage) => msg.type === "generation-progress" && msg.projectId === projectId
      );
      if (existingProgressIndex !== -1) {
        const existingProgress = state.messages[existingProgressIndex];
        // If it's still generating, skip duplicate
        if (existingProgress.status === "generating") {
          console.log(`⚠️ Generation already in progress for ${projectId}, skipping duplicate`);
          return;
        }
        // If it's completed/failed, remove the old one to make way for the new regeneration
        console.log(`🔄 Removing completed progress bar for ${projectId} to start fresh regeneration`);
        state.messages.splice(existingProgressIndex, 1);
      }
      // Add a generation progress message to the chat
      state.messages.push({
        sender: "system",
        type: "generation-progress",
        projectId,
        startTime: Date.now(),
        status: "generating",
        text: "Generating draft content...",
      } as ChatMessage);
    },
    updateGenerationStatus: (state, action: PayloadAction<UpdateGenerationStatusPayload>) => {
      const { projectId, status, error } = action.payload;
      // Find the generation-progress message for this project and update it
      const progressMessage = state.messages.find(
        (msg: ChatMessage) => msg.type === "generation-progress" && msg.projectId === projectId
      );
      if (progressMessage) {
        progressMessage.status = status;
        if (error) {
          progressMessage.error = error;
        }
      }
    },
    setBotTyping: (state, action: PayloadAction<boolean>) => {
      state.isBotTyping = action.payload;
    },
    setMcpEnvironment: (state, action: PayloadAction<string>) => {
      state.mcpEnvironment = action.payload;
    },
    setDataVersion: (state, action: PayloadAction<number>) => {
      state.dataVersion = action.payload;
    },
    setConversationId: (state, action: PayloadAction<string | null>) => {
      state.conversationId = action.payload;
    },
    setBootstrapInitialized: (state, action: PayloadAction<boolean>) => {
      state.bootstrapInitialized = action.payload;
    },
    setBootstrapError: (state, action: PayloadAction<string | null>) => {
      state.bootstrapError = action.payload;
    },
    removeMessageByType: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter((msg: ChatMessage) => msg.type !== action.payload);
    },
    resetChatForNewProject: (state) => {
      // Reset chat state for a new project while preserving welcome message
      state.messages = [{ sender: "chatbot", text: WELCOME_MESSAGE_TEXT }];
      state.isBotTyping = false;
      state.bootstrapInitialized = false;
      state.bootstrapError = null;
      state.conversationId = null;
      state.pendingStreamingMessages = {};
      state.currentWorkflowStatus = null;
      state.chatInputReady = true; // Unlock input for fresh projects
      state.useCaseSelected = false; // Reset use case selection for new project
      state.selectedUseCase = null; // Reset specific use case for new project
      state.pendingTurnResults = {}; // Phase 3: Clear staged results
      state.todoPlanByTurn = {}; // Clear todo plans
      // Note: mcpEnvironment and dataVersion are preserved
    },
    setWorkflowStatus: (state, action: PayloadAction<WorkflowStatus | null>) => {
      // Update workflow status for action "thinking" indicators
      state.currentWorkflowStatus = action.payload;
    },
    setChatInputReady: (state, action: PayloadAction<boolean>) => {
      state.chatInputReady = action.payload;
    },
    setUseCaseSelected: (state, action: PayloadAction<boolean>) => {
      state.useCaseSelected = action.payload;
    },
    setSelectedUseCase: (state, action: PayloadAction<string | null>) => {
      state.selectedUseCase = action.payload;
      // Also mark use case as selected when a specific use case is set
      if (action.payload) {
        state.useCaseSelected = true;
      }
    },
    stageTurnResults: (state, action: PayloadAction<StageTurnResultsPayload>) => {
      // Phase 3: Stage results payload for a turn to be merged when chat_response finalizes
      const { turnId, results, documents } = action.payload;
      if (turnId && state.pendingTurnResults) {
        state.pendingTurnResults[turnId] = { results, documents };
      }
    },
    setTurnTodoPlan: (state, action: PayloadAction<SetTurnTodoPlanPayload>) => {
      // Store todo plan for a turn, keyed by turnId
      const { turnId, plan, scopeSummary } = action.payload;
      console.log("📝 setTurnTodoPlan reducer called:", { turnId, plan, scopeSummary });
      if (turnId && plan && state.todoPlanByTurn) {
        state.todoPlanByTurn[turnId] = { plan, scopeSummary };
        console.log("✅ todoPlanByTurn updated:", state.todoPlanByTurn);
      } else {
        console.log("⚠️ setTurnTodoPlan: missing turnId or plan");
      }
    },
    clearTurnTodoPlan: (state, action: PayloadAction<ClearTurnTodoPlanPayload>) => {
      // Clear todo plan for a specific turn
      const { turnId } = action.payload;
      if (turnId && state.todoPlanByTurn && state.todoPlanByTurn[turnId]) {
        delete state.todoPlanByTurn[turnId];
      }
    },
    setChatHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.chatHistoryLoading = action.payload;
    },
    setChatHistoryError: (state, action: PayloadAction<string | null>) => {
      state.chatHistoryError = action.payload;
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      // Replace all messages with the provided array
      state.messages = action.payload;
    },
  },
});

export const {
  addMessage,
  setBotTyping,
  setMcpEnvironment,
  setDataVersion,
  setConversationId,
  setBootstrapInitialized,
  setBootstrapError,
  resetChatForNewProject,
  setWorkflowStatus,
  setChatInputReady,
  setUseCaseSelected,
  setSelectedUseCase,
  stageTurnResults,
  setTurnTodoPlan,
  clearTurnTodoPlan,
  addGenerationProgress,
  updateGenerationStatus,
  setChatHistoryLoading,
  setChatHistoryError,
  setMessages,
  removeMessageByType,
  updateMessageByClientId,
  removeMessageByClientId,
  updateMessageText,
  updateUploadInTable,
  clearUploadsTable,
} = chatSlice.actions;

// Helper action creators for chat input lock
export const lockChatInput = () => setChatInputReady(false);
export const unlockChatInput = () => setChatInputReady(true);

export default chatSlice.reducer;
